// Bot Manager v2 client layer (PRD §1.3, WP-07).
// Per-number bot settings, system action buttons, variables, error logs, and flow folders.
import { call } from './callable';

export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export type DayHours = { open: string; close: string } | 'off';

export interface BusinessHours {
  timezone: string;
  days: Record<DayOfWeek, DayHours>;
}

export interface WebhookTriggers {
  incoming: boolean;
  outgoing: boolean;
  status: boolean;
  conversationStatus: boolean;
}

export interface SharedInboxSettings {
  signatureEnabled: boolean;
  signature: string;
  disableBotOnTakeover: boolean;
  disableAiOnTakeover: boolean;
  autoEnableAfterMin: number;
  typingIndicator: boolean;
  inputFlowExpiryMin: number;
  humanChatExpiryMin: number;
  failedDeliveryLabelId?: string | null;
}

export interface BotGoogleSettings {
  sheetId?: string | null;
  contactsAccountId?: string | null;
}

export interface BotEmailSettings {
  profileId?: string | null;
  subject?: string | null;
  body?: string | null;
}

export interface BotSmsSettings {
  profileId?: string | null;
  body?: string | null;
}

export interface BotAutoresponderSettings {
  provider?: string | null;
  listId?: string | null;
}

export interface BotSettings {
  humanHandoffEmail?: string | null;
  noMatchReplyEnabled: boolean;
  botRepliesWhileAgentOnMobile: boolean;
  getStartedEnabled: boolean;
  getStartedFrequency: 'every_time' | 'once_a_day';
  webhookTriggers: WebhookTriggers;
  businessHours: BusinessHours;
  sharedInbox: SharedInboxSettings;
  google: BotGoogleSettings;
  email: BotEmailSettings;
  sms: BotSmsSettings;
  autoresponder: BotAutoresponderSettings;
  updatedAt?: number;
  updatedBy?: string | null;
}

export const SYSTEM_ACTION_KEYS = [
  'get_started',
  'no_match',
  'location_reply',
  'unsubscribe',
  'resubscribe',
  'chat_with_human',
  'chat_with_bot',
  'confirm_order',
  'cancel_order',
] as const;

export type SystemActionKey = typeof SYSTEM_ACTION_KEYS[number];
export type SystemActionKind = 'text' | 'template' | 'flow';

export interface SystemActionDoc {
  key: SystemActionKey;
  enabled: boolean;
  kind: SystemActionKind;
  text?: string | null;
  templateName?: string | null;
  templateLanguage?: string | null;
  flowId?: string | null;
  updatedAt: number;
  updatedBy?: string | null;
}

export interface CustomVariableDoc {
  id: string;
  name: string;
  key: string;
  value: string;
  description?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface BotErrorDoc {
  id: string;
  tenantId: string;
  integrationId: string;
  code: string;
  message: string;
  context?: Record<string, unknown> | null;
  at: number;
}

export interface FlowFolderDoc {
  id: string;
  tenantId: string;
  name: string;
  color?: string | null;
  createdAt: number;
  updatedAt: number;
}

// ── Time & Timezone Constants ────────────────────────────────────────────────

export const TIMEZONES = [
  'Asia/Muscat',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Amman',
  'Africa/Cairo',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'UTC',
];

export const TIME_STEPS_30MIN: string[] = (() => {
  const steps: string[] = [];
  for (let h = 0; h < 24; h++) {
    const hh = String(h).padStart(2, '0');
    steps.push(`${hh}:00`);
    steps.push(`${hh}:30`);
  }
  return steps;
})();

export function defaultBotSettings(): BotSettings {
  return {
    humanHandoffEmail: null,
    noMatchReplyEnabled: false,
    botRepliesWhileAgentOnMobile: true,
    getStartedEnabled: true,
    getStartedFrequency: 'every_time',
    webhookTriggers: {
      incoming: false,
      outgoing: false,
      status: false,
      conversationStatus: false,
    },
    businessHours: {
      timezone: 'Asia/Muscat',
      days: {
        mon: { open: '09:00', close: '18:00' },
        tue: { open: '09:00', close: '18:00' },
        wed: { open: '09:00', close: '18:00' },
        thu: { open: '09:00', close: '18:00' },
        fri: 'off',
        sat: { open: '09:00', close: '18:00' },
        sun: { open: '09:00', close: '18:00' },
      },
    },
    sharedInbox: {
      signatureEnabled: false,
      signature: '',
      disableBotOnTakeover: true,
      disableAiOnTakeover: true,
      autoEnableAfterMin: 60,
      typingIndicator: true,
      inputFlowExpiryMin: 30,
      humanChatExpiryMin: 120,
      failedDeliveryLabelId: null,
    },
    google: {
      sheetId: null,
      contactsAccountId: null,
    },
    email: {
      profileId: null,
      subject: null,
      body: null,
    },
    sms: {
      profileId: null,
      body: null,
    },
    autoresponder: {
      provider: null,
      listId: null,
    },
  };
}

// ── Callables ────────────────────────────────────────────────────────────────

export const botSettingsGet = call<
  { integrationId: string },
  { ok: boolean; settings: BotSettings }
>('botSettingsGet');

export const botSettingsSave = call<
  { integrationId: string; settings: Partial<BotSettings> },
  { ok: boolean; settings: BotSettings }
>('botSettingsSave');

export const systemActionGet = call<
  { integrationId: string; key: SystemActionKey },
  { ok: boolean; action: SystemActionDoc }
>('systemActionGet');

export const systemActionSave = call<
  {
    integrationId: string;
    action: {
      key: SystemActionKey;
      enabled: boolean;
      kind: SystemActionKind;
      text?: string | null;
      templateName?: string | null;
      templateLanguage?: string | null;
      flowId?: string | null;
    };
  },
  { ok: boolean; action: SystemActionDoc }
>('systemActionSave');

export const systemActionList = call<
  { integrationId: string },
  { ok: boolean; actions: SystemActionDoc[] }
>('systemActionList');

export const systemActionReset = call<
  { integrationId: string },
  { ok: boolean; actions: SystemActionDoc[] }
>('systemActionReset');

export const customVariableSave = call<
  {
    id?: string;
    name: string;
    key: string;
    value: string;
    description?: string | null;
  },
  { ok: boolean; variable: CustomVariableDoc }
>('customVariableSave');

export const customVariableDelete = call<
  { id: string },
  { ok: boolean }
>('customVariableDelete');

export const customVariableList = call<
  Record<string, never>,
  { ok: boolean; variables: CustomVariableDoc[] }
>('customVariableList');

export const botErrorList = call<
  { integrationId: string; limit?: number },
  { ok: boolean; errors: BotErrorDoc[] }
>('botErrorList');

export const flowFolderSave = call<
  { id?: string; name: string; color?: string | null },
  { ok: boolean; folder: FlowFolderDoc }
>('flowFolderSave');

export const flowFolderDelete = call<
  { id: string },
  { ok: boolean }
>('flowFolderDelete');

export const flowFolderList = call<
  Record<string, never>,
  { ok: boolean; folders: FlowFolderDoc[] }
>('flowFolderList');
